import { MW_BUCKETS, mwBucket } from '../game/scoring';
import type { TierRank } from '../game/types';
import type { FieldData, FieldSubmission } from '../state/field';
import { str } from './strings';

/**
 * How the day's field did, worked out on the device (GDD §4.7).
 *
 * The server holds totals and nothing else — no percentile maths, no per-player
 * row — so everything comparative is computed here, from counters that name
 * nobody.
 */

/** Half the players are at or above the median, so this is where a standing starts. */
const STANDING_FLOOR = 50;

/** The tier ladder, top rung first, as the counters name it. */
const LADDER: readonly TierRank[] = ['superconductor', 'master', 'journeyman', 'apprentice'];

export interface FieldBand {
  label: string;
  pct: number;
  /** The player's own rank, the one row the panel marks and brightens. */
  you: boolean;
}

export interface FieldView {
  players: number;
  /**
   * The share of the field this player is above, or `null` below the median.
   *
   * A player at or above it reads their standing; a player below it reads a
   * fact about the grid instead. The readout exists to place a score, and no
   * version of placing it needs to be a scolding (GDD §4.7).
   */
  standing: number | null;
  bands: FieldBand[];
  avgMW: number | null;
  /** How many of the players kept the flawless mark, this reader counted once. */
  flawless: number;
  /** The day is still running, so nothing here is settled. */
  young: boolean;
}

/**
 * Where this reading falls in the field, as a percentage.
 *
 * The counters band megawatts by tens, so a player is placed against the bands
 * below theirs plus a linear share of their own. Inside the top band the share
 * is fixed at half: 91 and 100 are not interpolable when 100 is the ceiling.
 */
export function standingFor(hist: number[], n: number, mw: number): number {
  if (n <= 0) return 0;
  const band = mwBucket(mw);
  let below = 0;
  for (let i = 0; i < band; i++) below += hist[i] ?? 0;
  const within =
    band >= MW_BUCKETS - 1 ? 0.5 : Math.max(0, Math.min(1, (mw - (band * 10 + 1)) / 10));
  return Math.round(((below + (hist[band] ?? 0) * within) / n) * 100);
}

/** This player's own reading, as the readout counts it. */
export interface FieldMe {
  mw: number;
  tier: string;
  /** This result kept the flawless mark. Absent reads as false. */
  flawless?: boolean;
}

/** The counters the readout reads — the split, the average, the rare mark. */
type Counters = Pick<FieldData, 'n' | 'hist' | 'tiers' | 'mwSum' | 'mwN' | 'flawless'>;

/** No count is ever negative, however stale the snapshot it came from. */
const less = (n: number): number => Math.max(0, n - 1);

/**
 * The counters with this player in them, exactly once.
 *
 * The room's copy of a player is whatever they last sent it, which on a grid
 * they are still improving is behind the reading on their own card. So the
 * sent entry comes back out and the current one goes in: the player is counted
 * once, and reads themselves as they are rather than as they were.
 */
function withMe(data: FieldData, me: FieldMe, sent: FieldSubmission | null): Counters {
  const hist = Array.from({ length: MW_BUCKETS }, (_, i) => data.hist[i] ?? 0);
  const tiers = { ...data.tiers };
  const out: Counters = {
    n: data.n,
    hist,
    tiers,
    mwSum: data.mwSum,
    mwN: data.mwN,
    flawless: data.flawless,
  };

  if (sent) {
    out.n = less(out.n);
    hist[sent.bucket] = less(hist[sent.bucket] ?? 0);
    tiers[sent.tier] = less(tiers[sent.tier] ?? 0);
    out.mwSum = Math.max(0, out.mwSum - sent.mw);
    out.mwN = less(out.mwN);
    if (sent.flawless) out.flawless = less(out.flawless);
  }

  const band = mwBucket(me.mw);
  out.n += 1;
  hist[band] = (hist[band] ?? 0) + 1;
  tiers[me.tier] = (tiers[me.tier] ?? 0) + 1;
  out.mwSum += me.mw;
  out.mwN += 1;
  if (me.flawless) out.flawless += 1;
  return out;
}

/**
 * The readout, or `null` when the room is empty.
 *
 * The player is one of the players. They are in the total, in the tier split
 * and in the standing, because they played the grid the readout is about and a
 * room that leaves out the person standing in it is not the room (GDD §4.7).
 *
 * There is no floor: a thin field is described and states its size, and the
 * count it leads with is what keeps a small room honest. Only a field with
 * nobody in it — not even the reader — is silence.
 */
export function buildFieldView(
  data: FieldData | null,
  me: FieldMe,
  opts: {
    young: boolean;
    /** This result is in the counters, or is on its way to them. */
    mine?: boolean;
    /** What the counters already hold for this player, to be taken back out. */
    sent?: FieldSubmission | null;
  },
): FieldView | null {
  if (!data) return null;

  const room = opts.mine ? withMe(data, me, opts.sent ?? null) : data;
  if (room.n <= 0) return null;
  const standing = standingFor(room.hist, room.n, me.mw);
  const share = (count: number): number => Math.round((count / room.n) * 100);
  const bands = LADDER.map((key) => ({
    label: str().ranks[key],
    pct: share(room.tiers[key] ?? 0),
    you: key === me.tier,
  }));

  return {
    players: room.n,
    standing: standing >= STANDING_FLOOR ? standing : null,
    bands,
    avgMW: room.mwN > 0 ? Math.round(room.mwSum / room.mwN) : null,
    flawless: Math.max(0, room.flawless),
    young: opts.young,
  };
}

const count = (n: number): string => n.toLocaleString('en-US');

/**
 * One section of the panel: a header in the label's own idiom, then its rows.
 *
 * A section draws no box of its own. Both of them sit in the panel's single
 * grid, so every label, rail and value lines up on the same three columns
 * rather than each section measuring itself.
 *
 * Every bar draws at face value: the rail is the whole field, and the fill is
 * the share the number beside it states (STYLEGUIDE §7).
 *
 * The rail is decorative and says so: the number it draws is already text
 * beside it, so a screen reader gets the readout and skips the drawing.
 */
function section(header: string, rows: readonly FieldBand[]): string {
  const bars = rows
    .map((row, i) => {
      const width = row.pct;
      const you = row.you ? ' you' : '';
      // The player's own row is marked by weight and the accent — a change
      // in a slot every row already has, so nothing shifts (STYLEGUIDE
      // §12). A screen reader hears the sr-only word instead.
      const marker = row.you ? `<span class="sr-only">${str().fieldYou}</span>` : '';
      return (
        `<span class="field-row${you}" style="--row:${i}">` +
        `<i>${row.label}</i>` +
        marker +
        `<span class="field-rail" aria-hidden="true">` +
        `<span class="field-fill" style="width:${width}%"></span>` +
        `</span>` +
        `<b>${row.pct}%</b>` +
        `</span>`
      );
    })
    .join('');
  return `<span class="field-sect"><span class="field-head"><i>${header}</i></span>${bars}</span>`;
}

/**
 * The readout as it sits on the card: readout lines in the stat row's idiom
 * over one rule, then the one bar panel this interface draws — no pill, no
 * gradient, no glow, no emoji (STYLEGUIDE §7).
 *
 * `yesterday` is the settled line from the day before, when today's card has
 * one. It reads with the room's other numbers rather than below the buttons,
 * and it lands under the young line it answers: this is what "final" looks
 * like. The card's own last word stays the countdown (STYLEGUIDE §7).
 */
export function renderFieldLines(view: FieldView, yesterday?: string | null): string[] {
  // The label names the block; the rest of the head is one clause, not two
  // facts joined by a · — the room's size and the reader's place in it are not
  // parallel, and a `·` between them reads as two fragments (STYLEGUIDE §7).
  const head =
    view.standing === null
      ? `<i>${str().fieldName}</i> ${str().fieldPlayed(count(view.players))}`
      : `<i>${str().fieldName}</i> ${str().fieldStanding(view.standing, count(view.players))}`;

  const lines = [`<span class="field-sum">${head}</span>`];
  if (view.avgMW !== null) {
    lines.push(`<i>${str().fieldAverage}</i> <b>${view.avgMW}</b> <i>${str().unitMW}</i>`);
  }
  // Stated rather than implied, and under the summary it qualifies: an
  // unsettled rate presented flatly is an overstated one (Pillar 4).
  if (view.young) lines.push(str().fieldYoung);
  if (yesterday) lines.push(yesterday);

  lines.push(`<span class="field-bars">${section(str().fieldRanks, view.bands)}</span>`);
  return lines;
}

/**
 * Yesterday's line on today's card.
 *
 * Same rule as the readout: at or above the median the player reads their own
 * standing, below it they read the day.
 */
export function yesterdayLine(view: FieldView | null): string | null {
  if (!view) return null;
  if (view.standing !== null) {
    return str().yesterdayStanding(view.standing, count(view.players));
  }
  const avg = view.avgMW !== null ? str().yesterdayAvg(view.avgMW) : '';
  return str().yesterdayPlayed(count(view.players), avg);
}
